import { useState } from "react";
import { toast } from "sonner";
import { Bookmark, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errText } from "@/lib/api";

export const SavedViews = ({ views, filters, onApply, onChanged }) => {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");

  const activeFilters = Object.entries(filters).filter(([, v]) => v && v !== "all").length;

  const save = async () => {
    if (!name.trim()) return toast.error("Give this view a name");
    try {
      await api.post("/saved-views", { name: name.trim(), filters });
      toast.success(`"${name.trim()}" saved`);
      setName("");
      setNaming(false);
      onChanged?.();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const remove = async (view) => {
    try {
      await api.delete(`/saved-views/${view.view_id}`);
      toast.success(`"${view.name}" removed`);
      onChanged?.();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="saved-views">
      <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        <Bookmark className="h-3.5 w-3.5 text-primary" /> Saved views
      </span>
      {views.map((v) => (
        <span
          key={v.view_id}
          className="group flex items-center gap-1 rounded-full border border-border pl-3.5 pr-1.5 py-1.5 text-[12px] font-bold transition-colors duration-200 hover:border-foreground"
          data-testid={`saved-view-${v.name}`}
        >
          <button onClick={() => onApply(v)} data-testid={`saved-view-apply-${v.name}`}>
            {v.name}
          </button>
          <button
            onClick={() => remove(v)}
            className="rounded-full p-1 text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-destructive"
            title={`Delete ${v.name}`}
            data-testid={`saved-view-delete-${v.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {views.length === 0 && !naming && (
        <span className="text-[12px] text-muted-foreground">
          Set some filters below, then save them here for one-click reuse
        </span>
      )}
      {naming ? (
        <span className="flex items-center gap-2">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder="e.g. Delhi site visits"
            className="h-9 w-[190px] rounded-lg"
            data-testid="saved-view-name-input"
          />
          <Button size="sm" className="h-9 rounded-lg font-bold" onClick={save} data-testid="saved-view-confirm-btn">
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-9 rounded-lg"
            onClick={() => {
              setNaming(false);
              setName("");
            }}
            data-testid="saved-view-cancel-btn"
          >
            Cancel
          </Button>
        </span>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="h-9 rounded-full font-bold"
          disabled={activeFilters === 0}
          title={activeFilters === 0 ? "Set a filter first" : "Save the current filters"}
          onClick={() => setNaming(true)}
          data-testid="saved-view-add-btn"
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Save current
        </Button>
      )}
    </div>
  );
};

export default SavedViews;
